import { CompletionDataExecutionTypeEnum, TrainingHistoryEntry } from '@/generated';
import { Box, Paper, useTheme } from '@mui/material';
import { PieCustomLayerProps, PieTooltipProps, ResponsivePie } from '@nivo/pie';
import { ExerciseStatsMode } from './TrainingStats';

type DistributionDatum = {
  id: CompletionDataExecutionTypeEnum;
  label: string;
  value: number;
};

const CustomTooltip = (props: PieTooltipProps<{}>) => (
  <Paper elevation={6} sx={{ width: 150, px: 1.25, py: 1, border: 1, borderColor: props.datum.color, textTransform: 'capitalize' }}>
    {props.datum.id}
  </Paper>
);

const TotalLayer = (props: PieCustomLayerProps<DistributionDatum> & { selected: boolean; onClick: () => void }) => {
  const theme = useTheme();
  const selectTotal = (event: React.KeyboardEvent<SVGGElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    props.onClick();
  };

  return (
    <g
      role="button"
      aria-label="Show total exercise statistics"
      aria-pressed={props.selected}
      tabIndex={0}
      onMouseDown={(event) => event.preventDefault()}
      onClick={props.onClick}
      onKeyDown={selectTotal}
      style={{ cursor: 'pointer' }}
    >
      <circle
        cx={props.centerX}
        cy={props.centerY}
        r={Math.max(0, props.innerRadius - 2)}
        fill={props.selected ? theme.palette.primary.main : theme.palette.background.paper}
        stroke={theme.palette.primary.main}
        strokeWidth={2}
      />
      <text
        x={props.centerX}
        y={props.centerY}
        textAnchor="middle"
        dominantBaseline="central"
        fill={props.selected ? theme.palette.primary.contrastText : theme.palette.text.primary}
        fontSize={14}
        fontWeight={600}
        pointerEvents="none"
      >
        Total
      </text>
    </g>
  );
};

export const ExerciseDistribution = (props: { selectedEntry?: TrainingHistoryEntry; selectedMode: ExerciseStatsMode; onClick: (mode: ExerciseStatsMode) => void }) => {
  const { selectedEntry, onClick } = props;
  const exercises = selectedEntry?.completedTraining?.rounds.flatMap((round) => round.exercises).filter((exercise) => exercise != null) ?? [];

  const counts = [
    exercises.filter((exercise) => exercise.completionData.executionType === CompletionDataExecutionTypeEnum.Reps).length,
    exercises.filter((exercise) => exercise.completionData.executionType === CompletionDataExecutionTypeEnum.Duration).length,
    exercises.filter((exercise) => exercise.completionData.executionType === CompletionDataExecutionTypeEnum.Intervals).length,
  ];
  const total = counts.reduce((sum, count) => sum + count, 0);
  const percentages = counts.map((count) => (total > 0 ? Math.round((count / total) * 100_000) / 1_000 : 0));

  if (total > 0) {
    const roundingDifference = Math.round((100 - percentages.reduce((sum, percentage) => sum + percentage, 0)) * 1_000) / 1_000;
    const largestCategoryIndex = counts.indexOf(Math.max(...counts));
    percentages[largestCategoryIndex] += roundingDifference;
  }

  const data = [
    { id: CompletionDataExecutionTypeEnum.Reps, label: 'reps based', value: percentages[0] },
    { id: CompletionDataExecutionTypeEnum.Duration, label: 'duration based', value: percentages[1] },
    { id: CompletionDataExecutionTypeEnum.Intervals, label: 'interval based', value: percentages[2] },
  ];
  const totalLayer = (layerProps: PieCustomLayerProps<DistributionDatum>) => (
    <TotalLayer {...layerProps} selected={props.selectedMode === 'total'} onClick={() => onClick('total')} />
  );

  return (
    <Paper variant="outlined" sx={{ width: '100%', minWidth: 0, p: 1 }}>
      <Box sx={{ position: 'relative', width: '100%', height: 300 }}>
        <ResponsivePie /* or Pie for fixed dimensions */
          data={data}
          margin={{ left: 60, right: 60 }}
          innerRadius={0.5}
          padAngle={0.6}
          colors={{ scheme: 'dark2' }}
          theme={{ text: { fill: 'white' } }}
          cornerRadius={2}
          layers={['arcs', totalLayer, 'arcLinkLabels', 'arcLabels', 'legends']}
          onClick={(el) => {
            onClick(el.id as ExerciseStatsMode);
          }}
          activeOuterRadiusOffset={8}
          arcLinkLabelsSkipAngle={10}
          arcLinkLabel={(datum) => `${String(datum.id).charAt(0).toUpperCase()}${String(datum.id).slice(1)}`}
          arcLinkLabelsDiagonalLength={8}
          arcLinkLabelsStraightLength={8}
          arcLinkLabelsTextOffset={4}
          arcLinkLabelsThickness={2}
          arcLabelsSkipAngle={10}
          arcLabel={(datum) => `${datum.value.toFixed(3)}%`}
          tooltip={CustomTooltip}
        />
      </Box>
    </Paper>
  );
};
